"use client";

import { LuThermometerSnowflake } from "react-icons/lu";
import { Image } from "@/app/components/Image";
import { ScrollReveal } from "@/app/components/ScrollReveal";
import { Container, Eyebrow, RuleBlock, Section } from "@/app/components/SiteUi";
import { useI18n } from "@/lib/i18n";

const charts = [
	{ src: "/pictures/new/temp_profile_new.png", captionKey: "item_temp_profile", old: false },
	{ src: "/pictures/new/laser_sd_chart.png", captionKey: "item_depth1", old: false },
	{ src: "/pictures/new/sd_with_tp_bg.png", captionKey: "item_depth2", old: false },
	{ src: "/pictures/grof/snjod1.png", captionKey: "item_depth3", old: true },
	{ src: "/pictures/grof/snjod3.png", captionKey: "item_depth4", old: true },
	{ src: "/pictures/new/top_sens_temp_chart.png", captionKey: "item_temp", old: false },
	{ src: "/pictures/new/SNOWPACK_model_chart.png", captionKey: "item_snowpack", old: false },
] as const;

const legend = [
	{ key: "legend_green", color: "#16a34a" },
	{ key: "legend_lightblue", color: "#3b82f6" },
	{ key: "legend_darkblue", color: "#1e3a8a" },
] as const;

export function GraphsIntro() {
	const { t } = useI18n();

	return (
		<>
			<Section
				theme="tint"
				className="border-b border-gray-200 pt-[calc(72px+clamp(48px,7vh,80px))] pb-[clamp(40px,6vh,64px)]"
			>
				<Container>
					<ScrollReveal className="block">
						<div className="grid grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-end gap-[clamp(40px,6vw,96px)] max-nav:grid-cols-1 max-nav:gap-6">
							<RuleBlock className="self-end pb-1">
								<Eyebrow>{t("navbar_about_snowsense.dashboard")}</Eyebrow>
								<h1 className="whitespace-nowrap text-[clamp(34px,3.6vw,56px)] font-bold leading-none tracking-[-0.02em] text-gray-900 max-nav:whitespace-normal">
									{t("graphs_page.title")}
								</h1>
							</RuleBlock>
							<figure className="ml-auto w-full max-w-[360px] max-nav:ml-0">
								<Image
									src="/pictures/new/SM4.png"
									alt={t("graphs_page.diagram_caption")}
									className="h-auto w-full rounded-lg object-contain max-nav:max-h-[340px] max-nav:object-left"
								/>
								<figcaption className="mt-3 text-[16px] italic text-gray-500">
									{t("graphs_page.diagram_caption")}
								</figcaption>
							</figure>
						</div>
					</ScrollReveal>
				</Container>
			</Section>

			<Section theme="light">
				<Container className="pb-[clamp(24px,4vh,40px)]">
					{charts.map(({ src, captionKey, old }, index) => {
						const text = t(`graphs_page.${captionKey}`);
						// captions are "Title: text", split at the first colon
						const [title, ...rest] = text.split(":");
						const body = rest.join(":").trim();
						return (
							<ScrollReveal key={src} delay={index * 80} className="block">
								<article className="grid grid-cols-[minmax(0,4fr)_minmax(0,8fr)] items-stretch gap-[clamp(32px,5vw,80px)] border-t border-gray-200 py-[clamp(36px,5vh,56px)] first:border-0 max-nav:grid-cols-1 max-nav:gap-6">
									<div className="flex h-full flex-col justify-center gap-3">
										<div className="flex flex-wrap items-center gap-3">
											<span className="text-[15px] font-semibold tracking-[0.14em] text-primary-400">
												{String(index + 1).padStart(2, "0")}
											</span>
											{old && (
												<span className="inline-block rounded-full border border-line px-2.5 py-0.5 text-[13px] font-semibold uppercase tracking-[0.08em] text-gray-500">
													{t("graphs_page.old_chart_badge")}
												</span>
											)}
										</div>
										{rest.length > 0 && (
											<h2 className="text-[clamp(26px,2.4vw,36px)] font-bold leading-[1.15] tracking-[-0.015em] text-gray-900 text-balance">
												{title}
											</h2>
										)}
										<p className="max-w-[42ch] text-[18px] leading-[1.6] text-gray-600 first-letter:uppercase">
											{rest.length > 0 ? body : text}
										</p>
										{old && (
											<p className="mt-auto pt-6 text-[15px] leading-[1.5] text-gray-500">
												{t("graphs_page.old_chart_note")}
											</p>
										)}
									</div>
									<figure className="overflow-hidden rounded-lg border border-gray-200 bg-white p-3 shadow-[0_12px_32px_rgba(41,51,92,.08)]">
										<Image src={src} alt="" className="h-auto w-full" />
									</figure>
								</article>
							</ScrollReveal>
						);
					})}
				</Container>
			</Section>
		</>
	);
}

export function MapIntro() {
	const { t } = useI18n();

	return (
		<ScrollReveal className="block">
			<div className="grid grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-start gap-[clamp(32px,5vw,80px)] max-nav:grid-cols-1 max-nav:gap-6">
				<RuleBlock className="pt-[22px]">
					<p className="text-[18px] leading-[1.55] text-gray-600">{t("graphs_page.map_intro")}</p>
				</RuleBlock>
				<ul className="divide-y divide-line border-y border-line">
					{legend.map(({ key, color }) => (
						<li
							key={key}
							className="grid grid-cols-[48px_minmax(0,1fr)] items-center gap-3 py-[22px] text-[18px] leading-[1.45] text-gray-900"
						>
							<LuThermometerSnowflake size={40} color={color} aria-hidden />
							<p>{t(`graphs_page.${key}`)}</p>
						</li>
					))}
				</ul>
			</div>
			<p className="mt-6 text-[16px] text-gray-500">{t("graphs_page.map_cta")}</p>
		</ScrollReveal>
	);
}
